import React from 'react';
import { motion } from 'framer-motion';
import { Database, DollarSignIcon, PackageIcon, TargetIcon, TruckIcon, XCircleIcon, CheckCircleIcon, ArrowRightIcon, GaugeIcon, PercentIcon, CreditCardIcon, PieChartIcon, StoreIcon, UsersIcon } from 'lucide-react';
import type { DashboardInputs, DashboardMetrics, ArgentinaInputs, CountryCode } from '../utils/calculations';
import { formatCurrency, formatNumber } from '../utils/calculations';
interface InputCardProps {
  inputs: DashboardInputs;
  metrics: DashboardMetrics;
  onChange: (field: keyof DashboardInputs, value: number) => void;
  arInputs?: ArgentinaInputs;
  onArChange?: (field: keyof ArgentinaInputs, value: number) => void;
  country: CountryCode;
}
const fields: {
  key: keyof DashboardInputs;
  label: string;
  icon: React.ElementType;
  min: number;
  max: number;
  step: number;
  isCurrency: boolean;
}[] = [{
  key: 'price',
  label: 'Precio de Venta',
  icon: DollarSignIcon,
  min: 10000,
  max: 300000,
  step: 500,
  isCurrency: true
}, {
  key: 'productCost',
  label: 'Costo de Producto',
  icon: PackageIcon,
  min: 1000,
  max: 150000,
  step: 500,
  isCurrency: true
}, {
  key: 'fleteInicial',
  label: 'Costo Flete Inicial',
  icon: TruckIcon,
  min: 1000,
  max: 50000,
  step: 500,
  isCurrency: true
}, {
  key: 'cpaIdeal',
  label: 'CPA en Meta Ads',
  icon: TargetIcon,
  min: 1000,
  max: 50000,
  step: 500,
  isCurrency: true
}, {
  key: 'cancelledPct',
  label: '% Pedidos Cancelados',
  icon: XCircleIcon,
  min: 0,
  max: 50,
  step: 1,
  isCurrency: false
}, {
  key: 'deliveredPct',
  label: '% Pedidos Entregados',
  icon: CheckCircleIcon,
  min: 30,
  max: 100,
  step: 1,
  isCurrency: false
}];
const mxFields: {
  key: keyof DashboardInputs;
  label: string;
  icon: React.ElementType;
  min: number;
  max: number;
  step: number;
  isCurrency: boolean;
}[] = [{
  key: 'price',
  label: 'Precio de Venta',
  icon: DollarSignIcon,
  min: 100,
  max: 5000,
  step: 10,
  isCurrency: true
}, {
  key: 'productCost',
  label: 'Costo de Producto',
  icon: PackageIcon,
  min: 50,
  max: 3000,
  step: 10,
  isCurrency: true
}, {
  key: 'fleteInicial',
  label: 'Costo Flete Inicial',
  icon: TruckIcon,
  min: 10,
  max: 1000,
  step: 10,
  isCurrency: true
}, {
  key: 'cpaIdeal',
  label: 'CPA en Meta Ads',
  icon: TargetIcon,
  min: 10,
  max: 1000,
  step: 10,
  isCurrency: true
}, {
  key: 'cancelledPct',
  label: '% Pedidos Cancelados',
  icon: XCircleIcon,
  min: 0,
  max: 50,
  step: 1,
  isCurrency: false
}, {
  key: 'deliveredPct',
  label: '% Pedidos Entregados',
  icon: CheckCircleIcon,
  min: 30,
  max: 100,
  step: 1,
  isCurrency: false
}];
const arFields: {
  key: keyof ArgentinaInputs;
  label: string;
  icon: React.ElementType;
  min: number;
  max: number;
  step: number;
  isCurrency: boolean;
}[] = [{
  key: 'price',
  label: 'Precio de Venta',
  icon: DollarSignIcon,
  min: 5000,
  max: 200000,
  step: 1000,
  isCurrency: true
}, {
  key: 'productCost',
  label: 'Costo de Producto',
  icon: PackageIcon,
  min: 1000,
  max: 100000,
  step: 1000,
  isCurrency: true
}, {
  key: 'costoEnvio',
  label: 'Costo de Envío',
  icon: TruckIcon,
  min: 0,
  max: 30000,
  step: 500,
  isCurrency: true
}, {
  key: 'mercadoPagoPct',
  label: 'Mercado Pago (%)',
  icon: CreditCardIcon,
  min: 0,
  max: 15,
  step: 0.1,
  isCurrency: false
}, {
  key: 'comisionCuotasPct',
  label: 'Comisión Cuotas (%)',
  icon: PieChartIcon,
  min: 0,
  max: 50,
  step: 0.5,
  isCurrency: false
}, {
  key: 'clientesCuotasPct',
  label: '% Clientes en Cuotas',
  icon: UsersIcon,
  min: 0,
  max: 100,
  step: 1,
  isCurrency: false
}, {
  key: 'comisionTiendaPct',
  label: 'Comisión Tienda (%)',
  icon: StoreIcon,
  min: 0,
  max: 10,
  step: 0.1,
  isCurrency: false
}];
export function InputCard({
  inputs,
  metrics,
  onChange,
  arInputs,
  onArChange,
  country
}: InputCardProps) {
  const isAr = country === 'AR' && arInputs && onArChange;
  const isMx = country === 'MX';
  const handleNumberInput = (key: string, raw: string, isCurrency: boolean) => {
    let num: number;
    if (isCurrency) {
      // Currency fields: dots are thousands separators (es-CO, es-AR locales)
      // Strip dots, replace comma with dot for decimal, keep digits
      const cleaned = raw.replace(/\./g, '').replace(',', '.').replace(/[^0-9.]/g, '');
      num = parseFloat(cleaned);
    } else {
      // Percentage fields: dot or comma is decimal separator
      const cleaned = raw.replace(',', '.').replace(/[^0-9.]/g, '');
      num = parseFloat(cleaned);
    }
    if (!isNaN(num)) {
      if (isAr) {
        onArChange(key as keyof ArgentinaInputs, num);
      } else {
        onChange(key as keyof DashboardInputs, num);
      }
    } else if (raw.replace(/[^0-9.,]/g, '') === '') {
      if (isAr) {
        onArChange(key as keyof ArgentinaInputs, 0);
      } else {
        onChange(key as keyof DashboardInputs, 0);
      }
    }
  };
  const currentFields = isAr ? arFields : isMx ? mxFields : fields;
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.5,
    delay: 0.1
  }} className="glass-card p-6 h-full flex flex-col" data-id="element-153">
      <div className="flex items-center gap-3 mb-5" data-id="element-154">
        <div className="p-2 bg-white/5 rounded-lg" data-id="element-155">
          <Database className="w-5 h-5 text-white/70" data-id="element-156" />
        </div>
        <h2 className="text-sm font-bold tracking-widest text-white/70 uppercase" data-id="element-157">
          Datos de Entrada
        </h2>
      </div>

      <div className="space-y-3 flex-grow flex flex-col justify-center" data-id="element-158">
        {currentFields.map(field => {
        const value = isAr ? arInputs[field.key as keyof ArgentinaInputs] : inputs[field.key as keyof DashboardInputs];
        const pct = (value - field.min) / (field.max - field.min) * 100;
        return <div key={field.key} data-id="element-159">
              <div className="group rounded-xl p-2.5 hover:bg-white/5 transition-colors" data-id="element-160">
                <div className="flex items-center justify-between mb-1.5" data-id="element-161">
                  <div className="flex items-center gap-2" data-id="element-162">
                    <field.icon className="w-3.5 h-3.5 text-white/40 group-hover:text-salmon transition-colors flex-shrink-0" data-id="element-163" />
                    <div className="flex flex-col" data-id="element-164">
                      <span className="text-xs font-medium text-white/60" data-id="element-165">
                        {field.label}
                      </span>
                      {!isAr && field.key === 'deliveredPct' && <span className="text-[10px] font-semibold text-salmon/70 tracking-wider uppercase mt-0.5" data-id="element-166">
                          Efectividad Transportadora
                        </span>}
                    </div>
                  </div>
                  <input type="text" inputMode="decimal" value={field.isCurrency ? formatNumber(value, country) : value} onChange={e => handleNumberInput(field.key, e.target.value, field.isCurrency)} className="w-24 text-right bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-sm font-bold text-white focus:outline-none focus:border-salmon/50 transition-colors" data-id="element-167" />
                </div>
                <div className="relative" data-id="element-168">
                  <div className="absolute top-1/2 left-0 h-1.5 rounded-full bg-gradient-to-r from-salmon/60 to-salmon pointer-events-none -translate-y-1/2 z-0" style={{
                width: `${Math.max(0, Math.min(100, pct))}%`
              }} data-id="element-169" />
                  <input type="range" min={field.min} max={field.max} step={field.isCurrency ? field.step : 0.01} value={value} onChange={e => {
                if (isAr) {
                  onArChange(field.key as keyof ArgentinaInputs, parseFloat(e.target.value));
                } else {
                  onChange(field.key as keyof DashboardInputs, parseFloat(e.target.value));
                }
              }} className="relative z-10 w-full" data-id="element-170" />
                </div>
              </div>

              {/* Flete Real + Costo Devoluciones after Flete Inicial */}
              {!isAr && field.key === 'fleteInicial' && <div className="mx-2.5 mt-1 mb-1 space-y-1" data-id="element-171">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.02] border border-white/5" data-id="element-172">
                    <ArrowRightIcon className="w-3 h-3 text-white/30 flex-shrink-0" data-id="element-173" />
                    <span className="text-[10px] font-medium text-white/40" data-id="element-174">
                      Costo Devoluciones
                    </span>
                    <span className="ml-auto text-xs font-bold text-white/50" data-id="element-175">
                      {formatCurrency(metrics.costoDevoluciones, country)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.02] border border-salmon/10" data-id="element-176">
                    <ArrowRightIcon className="w-3 h-3 text-salmon/50 flex-shrink-0" data-id="element-177" />
                    <span className="text-[10px] font-medium text-white/40" data-id="element-178">
                      Flete Real
                    </span>
                    <span className="ml-auto text-xs font-bold text-salmon/80" data-id="element-179">
                      {formatCurrency(metrics.fleteReal, country)}
                    </span>
                  </div>
                </div>}
            </div>;
      })}

        {/* Fixed IIBB for Argentina */}
        {isAr && <div className="mx-2.5 mt-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5" data-id="element-180">
            <PercentIcon className="w-3.5 h-3.5 text-white/30 flex-shrink-0" data-id="element-181" />
            <span className="text-xs font-medium text-white/40" data-id="element-182">
              IIBB (fijo)
            </span>
            <span className="ml-auto text-sm font-bold text-white/50" data-id="element-183">
              3.00%
            </span>
          </div>}

        {/* Calculated Efficiency */}
        {!isAr && <div className="mx-2.5 mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-cyber-green/5 border border-cyber-green/10" data-id="element-184">
            <GaugeIcon className="w-4 h-4 text-cyber-green/60 flex-shrink-0" data-id="element-185" />
            <div className="flex flex-col" data-id="element-186">
              <span className="text-[10px] font-bold text-cyber-green/60 uppercase tracking-wider" data-id="element-187">
                Efectividad Real
              </span>
              <span className="text-[9px] text-white/30" data-id="element-188">
                (1 - {metrics.cancelledPct}%) × {metrics.deliveredPct}%
              </span>
            </div>
            <span className="ml-auto text-lg font-black text-cyber-green" data-id="element-189">
              {metrics.efectividadReal.toFixed(1)}%
            </span>
          </div>}
      </div>
    </motion.div>;
}